import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AddH265CamerasRequest, CustomCameraNameRequestModel, H265Camera, ILiveViewModelData, IWowzaResponse, PtzInfo, SiteWithCameraCount } from '../models/live-view.model';
import { IPagedSiteModelData, ISiteAllScheduleModelData, ISiteModelData, ISiteScheduleModelData, NoFlyZoneSchedule, SiteCameraModel, SitesFilterModel, TenantsPropertyDto } from '../models/sites.model';
import { IIncidentCountModelData, IncidentMetricsModelData, IncidentMetricsModelDataResult, IncidentModelData, IncidentReportModelData, IncidentVideoModelData, IncidentsFilterModel, IncidentsResultModel, NewIncidentMetrics } from '../models/incidents.model';
import { ITenantStatisticsModelData } from '../models/tenant.model';
import { ContactRequest, CustomerContactsFilterModel, IPagedSiteContact, SiteAlert, SiteTimezone } from '../models/contact.model';
import { FavoriteCameraModel, FavoriteCameraRequestModel, FavoriteViewModel, FavoriteViewRequestModel } from '../models/favorite-view.model';
import { CookieService } from 'ngx-cookie-service';
import { DefaultLayoutOption } from '../models/user-setting.model';
import { IServiceTicketsModelData, ServiceTicketsFilterModel } from '../models/service-tickets.model';
import { TypeValue } from '../../../_shared/core/models/type-value.model';
import { EscalationTypeSFResponseDto, IIncidentReportCountModelData, IncidentDetailsLogReportResult, IncidentLogReportResult, RetrieveIncidentDetails, SearchIncidentParameter } from '../models/incidents-report.model';
import { OrchidMotionEventResponseDto, PlaybackCameraDetails, PlaybackMotionDto, PlaybackRequestDto, PlaybackStreamResponseDto, PlaybackStreamsParameter } from '../models/playback.model';
import { BRANDING } from 'src/app/_shared/core/constants/branding.constants';
import { GetDateRangeForDevicePayload, GetDateRangeForDeviceResponse, GetTimelapseImagesPayload, GetTimelapseImagesResponse, TimelapseCameraDetails } from '../models/timelapse.model';
import { MonthlyDashboardFilterModel, MonthlyDashboardResponseModel } from '../models/monthly-dashboard.model';
import { AssetDashboardFilterModel, AssetDashboardResponseModel } from '../models/asset-dashboard.model';
import { AddEquipmentsPayload, CreateOpportunityResponseModel, GetEquipmentsResponseModel } from 'src/app/account/core/models/equipments.model';


const API_BASE_URL = environment.apiUrl;
@Injectable({
  providedIn: 'root'
})
export class MainHttpService {

  constructor(private readonly http: HttpClient,
    private readonly externalClient: HttpClient,
    private readonly handler: HttpBackend, // Used to bypass interceptors
    private readonly cookieService: CookieService) {
    // This client will NOT pass your global Auth/Tenant headers
    this.externalClient = new HttpClient(handler);
  }

  getAllSitesList(): Observable<any> {
    let url = API_BASE_URL + "api/sites";
    url = url.replace(/[?&]$/, "");
    return this.http.get<any>(url);
  }

  getCamerasBySiteId(siteId: number): Observable<any> {
    let url = API_BASE_URL + "api/sites/" + siteId + "/distinctcameras";
    url = url.replace(/[?&]$/, "");
    return this.http.get<any>(url);
  }

  getNvrBySiteId(siteId: number): Observable<any> {
    let url = API_BASE_URL + "api/nvr/sites/" + siteId;
    url = url.replace(/[?&]$/, "");
    return this.http.get<any>(url);
  }


  getFavoriteViewCameraBySiteId(siteId: number): Observable<any> {
    let url = API_BASE_URL + "api/services/app/FavoriteCameraSettings/GetFavoriteById"
    url = url.replace(/[?&]$/, "");

    let parameters = new HttpParams()
      .set('favoriteId', siteId.toString());

    return this.http.get<any>(url, { params: parameters });
  }

  getSiteWithCameraCount(): Observable<SiteWithCameraCount[]> {
    let url = API_BASE_URL + "api/sites/GetSiteWithCameraCount";
    url = url.replace(/[?&]$/, "");

    let parameters = new HttpParams()
      .set('isActive', true)
      .set('skipCameraCount', true);
    return this.http.get<SiteWithCameraCount[]>(url, { params: parameters });
  }

  //HTTP Calls for Main Page

  //Begin: Dashboard Page
  //Get tenant statistics
  getTenantStatistics(): Observable<ITenantStatisticsModelData> {
    let url = API_BASE_URL + "api/shared-site/dashboard-customer-statistics";
    url = url.replace(/[?&]$/, "");
    return this.http.get<ITenantStatisticsModelData>(url);
  }
  //Get lifetime metrics
  getIncidentMetrics(): Observable<IncidentMetricsModelData> {
    let url = API_BASE_URL + "api/incidents/dashboard/type-counts";
    url = url.replace(/[?&]$/, "");
    return this.http.get<IncidentMetricsModelData>(url);
  }

  getRecentIncidentMetrics(days: number): Observable<IncidentMetricsModelData> {
    let url = API_BASE_URL + "api/incidents/dashboard/type-counts?incidentDays=" + days.toString();
    url = url.replace(/[?&]$/, "");
    return this.http.get<IncidentMetricsModelData>(url);
  }

  getIncidentMetricsByParams(newMetrics: NewIncidentMetrics): Observable<IncidentMetricsModelDataResult> {
    let url = API_BASE_URL + "api/incidents/dashboard/retrieve-incident-counts";
    url = url.replace(/[?&]$/, "");
    return this.http.post<IncidentMetricsModelDataResult>(url, newMetrics);
  }

  getMonthlyIncidentCountByType(filter: IncidentsFilterModel): Observable<IIncidentCountModelData> {
    let parameters = new HttpParams();

    if (filter.incidentTypeIds && filter.incidentTypeIds.length > 0) {
      filter.incidentTypeIds.forEach(id => {
        parameters = parameters.append('incidentTypeIds', id.toString());
      });
    }

    if (filter.fromDate) {
      parameters = parameters.set('incidentStartDate', filter.fromDate);
    }

    if (filter.toDate) {
      parameters = parameters.set('incidentEndDate', filter.toDate);
    }

    if (filter.searchText) {
      parameters = parameters.set('search', filter.searchText);
    }


    let url = API_BASE_URL + "api/incidents/dashboard/month/type-counts";
    url = url.replace(/[?&]$/, "");
    return this.http.get<IIncidentCountModelData>(url, { params: parameters });
  }
  //End: Dashboard Page

  //Begin: Sites List Page
  //get all sites by user
  getAllSites(): Observable<ISiteModelData> {
    let url = API_BASE_URL + "api/shared-site/sites-by-user";
    url = url.replace(/[?&]$/, "");
    return this.http.get<ISiteModelData>(url);
  }

  getAllSitesWithPagination(filter: SitesFilterModel): Observable<IPagedSiteModelData> {
    let url = API_BASE_URL + "api/Sites/GetSiteswithPagination";
    url += `?skipCount=${filter.skipCount}`
    if (filter.maxResultCount) {
      url += `&maxResultCount=${filter.maxResultCount}`
    }
    if (filter.searchText) {
      url += `&searchText=${filter.searchText}`
    }
    url = url.replace(/[?&]$/, "");
    return this.http.get<IPagedSiteModelData>(url);
  }

  getSiteCameras(siteId): Observable<SiteCameraModel> {
    let url = API_BASE_URL + `api/Sites/${siteId}/dropdowncameras`;
    url = url.replace(/[?&]$/, "");
    return this.http.get<SiteCameraModel>(url);
  }

  getAllSitesAndMap(): Observable<ISiteModelData> {
    let url = API_BASE_URL + "api/services/app/SiteMap/GetSiteInfoAndMap";
    url = url.replace(/[?&]$/, "");
    return this.http.get<ISiteModelData>(url);
  }

  getAllSitesByTenant(): Observable<TenantsPropertyDto[]> {
    let url = API_BASE_URL + "api/shared-site/sites-by-tenant";
    url = url.replace(/[?&]$/, "");
    return this.http.get<TenantsPropertyDto[]>(url);
  }

  //get all scheduels by user
  getMonitoringScheduleForDay(startDate: moment.Moment, endDate: moment.Moment): Observable<ISiteScheduleModelData> {
    let parameters = new HttpParams()
      .set('startTime', startDate.format('YYYY-MM-DDTHH:mm:ss.SSSS[Z]'))
      .set('endTime', endDate.format('YYYY-MM-DDTHH:mm:ss.SSSS[Z]'));

    let url = API_BASE_URL + "api/services/app/Site/GetAllSiteSchedulesByUser";
    url = url.replace(/[?&]$/, "");
    return this.http.get<ISiteScheduleModelData>(url, { params: parameters });
  }

  getSiteSchedules(siteId: number, startDate: moment.Moment, endDate: moment.Moment): Observable<any> {
    let parameters = new URLSearchParams();
    parameters.append("siteId", siteId.toString());
    parameters.append("StartTime", startDate.format("YYYY-MM-DDTHH:mm:ss.SSSS[Z]"));
    parameters.append("EndTime", endDate.format("YYYY-MM-DDTHH:mm:ss.SSSS[Z]"));

    let url = API_BASE_URL + "api/services/app/Site/v2/GetSiteSchedule?" + parameters.toString();
    url = url.replace(/[?&]$/, "");
    return this.http.get<ISiteAllScheduleModelData>(url);
  }

  getNoFlyZoneSchedule(siteId: number): Observable<any> {
    let url = API_BASE_URL + "api/services/app/Site/GetNoFlyZoneSchedule?" + "siteID=" + siteId.toString();
    url = url.replace(/[?&]$/, "");
    return this.http.get<NoFlyZoneSchedule>(url);
  }
  //End: Sites List Page

  //Start: Live View Page   
  //get all cameras by user
  getAllSiteDetailsAndCameras(): Observable<ILiveViewModelData> {
    let url = API_BASE_URL + "api/services/app/Camera/GetAllSiteDetailsAndCameras";
    url = url.replace(/[?&]$/, "");
    return this.http.get<ILiveViewModelData>(url);
  }
  //End: Sites List

  //#region INCIDENT REPORTS
  getSFIncidents(filter?: SearchIncidentParameter): Observable<IncidentLogReportResult> {
    let url = `${API_BASE_URL}api/incidents/search-sf-incident`;

    if (filter) {
        if (filter.search) {
          filter.search = filter.search.replace(/'/g, '\\u0027');
        }
        const queryString = filter.toQueryString();
        url = url + queryString; 
    }

    return this.http.get<IncidentLogReportResult>(url);
  }

  getSFIncidentById(incidentId: string, filter?: RetrieveIncidentDetails): Observable<IncidentDetailsLogReportResult> {
    let url = `${API_BASE_URL}api/incidents/sf-incident/${incidentId}`;

    if (filter) {
      const queryString = filter.toQueryString();
      url = url + queryString; 
    }

    return this.http.get<IncidentDetailsLogReportResult>(url);
  }

  getSFEscalationTypes(): Observable<EscalationTypeSFResponseDto> {
    let url = `${API_BASE_URL}api/incidents/sf-escalation-types`;

    return this.http.get<EscalationTypeSFResponseDto>(url);
  }

  getYearlySFIncidentsCountAsync(filter?: SearchIncidentParameter): Observable<IIncidentReportCountModelData> {
    let url = `${API_BASE_URL}api/incidents/year/sf-incident-count`;

    if (filter) {
      if (filter.search) {
          filter.search = filter.search.replace(/'/g, '\\u0027');
        }
      const queryString = filter.toQueryString();
      url = url + queryString; 
    }
    
    return this.http.get<IIncidentReportCountModelData>(url);
  }
  //#endregion INCIDENT REPORTS

  //#region INCIDENTS
  //Begin: Incidents
  //Get all incidents
  getAllIncidents(filter: IncidentsFilterModel): Observable<IncidentsResultModel> {
    let url_ = API_BASE_URL + 'api/incidents/pagination';
    url_ = url_ + `?skipCount=${filter.skipCount}`;

    if (filter.maxResultCount) {
      url_ = url_ + `&maxResultCount=${filter.maxResultCount}`;
    }

    if (filter.fromDate) {
      url_ = url_ + `&incidentStartDate=${filter.fromDate}`
    }

    if (filter.toDate) {
      url_ = url_ + `&incidentEndDate=${filter.toDate}`
    }

    if (filter.incidentTypeIds) {
      for (const element of filter.incidentTypeIds) {
        url_ = url_ + `&incidentTypeIds=${element}`
      }
    }
    url_ = url_ + `&search=${filter.searchText}`

    if (filter.sortByString) {
      url_ = url_ + `&sorting=${filter.sortByString}`
    }
    return this.http.get<IncidentsResultModel>(url_);
  }

  getRecentIncidents(size: number): Observable<IncidentModelData> {
    let url = API_BASE_URL + 'api/incidents?maxResultCount=' + size.toString();
    url = url.replace(/[?&]$/, '');

    return this.http.get<IncidentModelData>(url);
  }

  getIncidentReport(incidentId: number): Observable<IncidentReportModelData> {
    let url = API_BASE_URL + 'api/incidents/' + incidentId.toString() + '/report';
    url = url.replace(/[?&]$/, '');
    return this.http.get<IncidentReportModelData>(url);
  }

  getIncidentVideos(incidentId: number): Observable<IncidentVideoModelData> {
    let url = API_BASE_URL + 'api/incidents/' + incidentId.toString() + '/videos';
    url = url.replace(/[?&]$/, '');
    return this.http.get<IncidentVideoModelData>(url);
  }
  //End: Incidents
  //#endregion INCIDENTS

  connectWowzaStreambyName(streamFileName: string, ipAddress: string, wowzaApplicationName: string = "connect") {
    const notFoundError = new Error('Not Found');
    let url = API_BASE_URL + "api/services/app/Wowza/ConnectWowzaStreamByName?";
    if (!streamFileName && !ipAddress) {
      return of(notFoundError);
    } else {
      url += 'streamFileName=' + encodeURIComponent(streamFileName) 
           + '&ipAddress=' + encodeURIComponent(ipAddress) 
           + '&wowzaApplicationName=' + encodeURIComponent(wowzaApplicationName);
    }
    url = url.replace(/[?&]$/, "");

    return this.http.post<IWowzaResponse>(url, null)
      .pipe(
        map((result: IWowzaResponse) => {
          if (!result) {
            return notFoundError;
          }
          return result;
        })
      );
  }

  ptz(ptzInfo: PtzInfo): Observable<void> {
    let url = `${API_BASE_URL}api/services/app/Camera/PTZ`;
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, ptzInfo);
  }

  disconnectWowzaStreambyName(streamFileName: string, ipAddress: string, wowzaApplicationName: string = "connect") {
    let url = API_BASE_URL + "api/services/app/Wowza/DisconnectWowzaStreamByName?";
    if (streamFileName && ipAddress) {
      url += 'streamFileName=' + encodeURIComponent(streamFileName) 
           + '&ipAddress=' + encodeURIComponent(ipAddress) 
           + '&wowzaApplicationName=' + encodeURIComponent(wowzaApplicationName);
    }
    url = url.replace(/[?&]$/, "");

    fetch(url, {
      keepalive: true,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.cookieService.get(environment.tokenKey)}`,
      }
    });
  }

  disconnectWowzaStreamsByTenant(tenantId: number): void {
    let tenantIdString = encodeURIComponent(tenantId.toString());
    let url = `${API_BASE_URL}api/services/app/Wowza/DisconnectWowzaStreams?tenantId=${tenantIdString}`;
    url = url.replace(/[?&]$/, "");

    fetch(url, {
      keepalive: true,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.cookieService.get(environment.tokenKey)}`,
      }
    });
  }
  //End: Live View Page

  //Start: Favorite Views
  getAllFavoriteViews(): Observable<FavoriteViewModel[]> {
    let url = API_BASE_URL + "api/FavoriteView/GetAllFavoriteViews";
    url = url.replace(/[?&]$/, "");

    return this.http.get<FavoriteViewModel[]>(url);
  }

  getFavoriteViewAndCameras(favoriteViewId: any): Observable<FavoriteCameraModel[]> {
    let url = API_BASE_URL + "api/FavoriteView/GetFavoriteViewAndCameras/" + favoriteViewId;
    url = url.replace(/[?&]$/, "");

    return this.http.get<FavoriteCameraModel[]>(url);
  }

  createFavoriteView(request: FavoriteViewRequestModel): Observable<void> {
    let url = API_BASE_URL + 'api/services/app/FavoriteCameraSettings/CreateFavoriteView';
    url = url.replace(/[?&]$/, '');

    return this.http.post<void>(url, request);
  }

  updateFavoriteView(request: FavoriteViewRequestModel): Observable<void> {
    let url = API_BASE_URL + 'api/services/app/FavoriteCameraSettings/UpdateFavoriteView';
    url = url.replace(/[?&]$/, '');

    return this.http.put<void>(url, request);
  }

  updateCustomCameraName(request: CustomCameraNameRequestModel): Observable<void> {
    let url = API_BASE_URL + 'api/Camera/SCName';
    url = url.replace(/[?&]$/, '');

    return this.http.put<void>(url, request);
  }

  deleteFavoriteView(favoriteViewId: number): Observable<void> {
    let parameters = new HttpParams()
      .set('favoriteViewId', favoriteViewId)

    let url = API_BASE_URL + "api/services/app/FavoriteCameraSettings/DeleteFavoriteView";
    url = url.replace(/[?&]$/, "");

    return this.http.delete<void>(url, { params: parameters });
  }

  createFavoriteCamera(request: FavoriteCameraRequestModel): Observable<void> {
    let url = API_BASE_URL + 'api/services/app/FavoriteCameraSettings/CreateFavoriteCamera';
    url = url.replace(/[?&]$/, '');

    return this.http.post<void>(url, request);
  }

  deleteFavoriteCamera(favoriteCameraId: number): Observable<void> {
    let parameters = new HttpParams()
      .set('favoriteCameraId', favoriteCameraId)

    let url = API_BASE_URL + 'api/services/app/FavoriteCameraSettings/DeleteFavoriteCamera';
    url = url.replace(/[?&]$/, '');

    return this.http.delete<void>(url, { params: parameters });
  }

  getH265CameraList(): Observable<H265Camera[]> {
    let url = API_BASE_URL + "api/Sites/GetH265Camera";
    url = url.replace(/[?&]$/, "");

    return this.http.get<H265Camera[]>(url);
  }

  addH265CamerasList(siteId: number, request: AddH265CamerasRequest): Observable<H265Camera[]> {
    let url = API_BASE_URL + 'api/Sites/' + encodeURIComponent('' + siteId) + '/AddH265CamerasBySite';
    url = url.replace(/[?&]$/, "");

    return this.http.put<H265Camera[]>(url, request);
  }

  //End: Favorite Views

  //Start: Contact Page
  sendRequest(request: ContactRequest): Observable<void> {
    let url = API_BASE_URL + 'api/Contact/CreateSFCase';
    url = url.replace(/[?&]$/, '');

    return this.http.post<void>(url, request);
  }
  //End: Contact Page

  // Start: User Settings
  setLayoutbyId(request: DefaultLayoutOption): Observable<boolean> {
    let url = API_BASE_URL + 'api/User/UserDefaultSettings';
    url = url.replace(/[?&]$/, "");
    return this.http.post<boolean>(url, request);
  }

  getLayoutbyId(userId: number): Observable<DefaultLayoutOption> {
    let url = API_BASE_URL + "api/User/UserDefaultSettings/"+userId;
    url = url.replace(/[?&]$/, "");

    return this.http.get<DefaultLayoutOption>(url);
  }
  // End: User Settings

  // Start: Service Tickets
  getServiceTickets(filter: ServiceTicketsFilterModel): Observable<IServiceTicketsModelData> {

    let url = API_BASE_URL + "api/services/app/ServiceTicket/GetSFServiceTickets";
    url = url.replace(/[?&]$/, "");

    url = url + `?siteId=${filter.siteId}`;
    
    if (filter.skipCount) {
      url = url + `&skipCount=${filter.skipCount}`;
    }

    if (filter.status) {
      url = url + `&status=${filter.status}`;
    }

    if (filter.maxResultCount) {
      url = url + `&maxResultCount=${filter.maxResultCount}`;
    }
    
    if (filter.searchText) {
      url = url + `&search=${filter.searchText}`;
    }

    if (filter.isVideoReview) {
      url = url + `&isVideoReview=${filter.isVideoReview}`;
    }

    return this.http.get<IServiceTicketsModelData>(url);
  }

  // End: Service Tickets

  //#region SITE PROTOCOLS

  /**
  * This is the get function for get the site protocols
  * @param id This is the id parameter
  * @returns returns the site protocols
  */
  getSiteProtocols(siteId: number): Observable<any> {
    let url = API_BASE_URL + 'api/v2/site-protocols/site/' + encodeURIComponent('' + siteId);
    url = url.replace(/[?&]$/, '');

    return this.http.get<any>(url);
  }

  //#endregion SITE PROTOCOLS

  //#region ESCALATION TYPES

  /**
  * This is the get function for getting the escalation type search list
  * @returns returns escalation type search list
  */
  getEscalationTypesSearchList(): Observable<any> {
    let url = API_BASE_URL + 'api/escalation-types?sorting=name';
    url = url.replace(/[?&]$/, "");

    return this.http.get<any>(url)
      .pipe(
        map((response: any) => {
          if (!response?.result) {
            throw new Error("Failed to retrieve escalation typesearch list");
          }

          return response.result;
        })
      );
  }

  //#endregion ESCALATION TYPES

  //#region GetDiffLookUpIdsValues

  /**
  * This is the get function to for getting multiple type value list by type id
  * @param idList This is the idList parameter
  * @param sorting This is the sorting parameter
  * @returns returns multiple type value list by type id
  */
  getMultipleTypeValuesByLookupIds(idList: number[], sorting?: string | undefined, removeNotSpecified?: boolean): Observable<TypeValue[]> {
    let url = API_BASE_URL + "api/types/values";

    let params = new HttpParams();
    for (let id of idList) {
      params = params.append('lookupId', id.toString());
    }

    if (sorting !== undefined) {
      params = params.append('sorting', sorting);
    }

    if (removeNotSpecified == undefined)
       params = params.append('removeNotSpecified', false);
    else
       params = params.append('removeNotSpecified', removeNotSpecified);

    return this.http.get<any>(url, {
      params: params,
    }).pipe(
      map((response: any) => {
        if (!response && !response.result) {
          throw new Error("Failed to retrieve type values");
        }
        return response.result;
      })
    );
  }

  //#endregion GetDiffLookUpIdsValues

  //#region GetProtocolSteps

  /**
 * This is the get function to return the protocol step type search list
 * @returns returns the protocol step type search list
 */
  getProtocolStepTypeSearchList(): Observable<any> {
    let url = API_BASE_URL + 'api/site-protocols/step/type';
    url = url.replace(/[?&]$/, "");

    return this.http.get<any>(url)
      .pipe(
        map((response: any) => {
          if (!response?.result) {
            throw new Error("Failed to retrieve type values");
          }

          return response.result;
        })
      );
  }

  //#endregion GetProtocolSteps

  //#region GetSiteStealthContacts
  /**
  * This is the get function for retrieving the Stealth Contacts assigned to site
  * @siteId This is the site id parameter
  * @returns returns list of Stealth Contacts assigned to the site
  */
  getSiteStealthContacts(siteId: number): Observable<any> {
    let url = API_BASE_URL + 'api/sites/' + siteId + '/stealth-contact';
    url = url.replace(/[?&]$/, '');

    return this.http.get<any>(url).pipe(
      map((response: any) => {
        if (!response) {
          throw new Error(
            `Failed to get ${BRANDING.COMPANY_NAME} contacts for site id: ` + siteId + '.'
          );
        }
        return response;
      })
    );
  }
  //#endregion GetSiteStealthContacts

  //#region GetSiteContacts
  /**
 * This is the get function for get the  site contacts
 * @param id This is the id parameter
 * @returns returns the  site contacts
 */
  getSiteContacts(id: number): Observable<any> {
    let url =
      API_BASE_URL + 'api/sites/' + encodeURIComponent('' + id) + '/contact';
    url = url.replace(/[?&]$/, '');

    return this.http.get<any>(url).pipe(
      map((response: any) => {
        if (!response) {
          throw new Error('Failed to retrieve  site contacts');
        }

        return response;
      })
    );
  }

  //#endregion GetSiteContacts

  //#region Authorities
  /**
   * This is the get function for getting all authorities
   * @param advancedFilters filter for retrieving authorities
   * @returns returns all authorities
   */
  getAuthorities(siteId:number): Observable<any> {
    let url = API_BASE_URL + 'api/authorities/site/'+siteId;
    url = url.replace(/[?&]$/, '');
    return this.http.get<any>(url).pipe(
      map((response: any) => {
        if (!response?.result) {
          throw new Error('Failed to retrieve authorities');
        }

        return response.result;
      })
    );
  }

  //#endregion Authorities

  //#region GetSiteAuthoritiesContacts
  /**
 * This is the get function for get the  site contacts
 * @param id This is the site id parameter
 * @returns returns the  site authorities contacts
 */
  getSiteAuthoritiesContacts(id: number): Observable<any> {
    let url =
      API_BASE_URL + 'api/sites/' + encodeURIComponent('' + id) + '/information';
    url = url.replace(/[?&]$/, '');

    return this.http.get<any>(url).pipe(
      map((response: any) => {
        if (!response) {
          throw new Error('Failed to retrieve  site authorities contacts');
        }

        return response;
      })
    );
  }

  //#endregion GetSiteAuthoritiesContacts
  
  //#region Playback
  
  getAvailableTimeStampWithDigest(playbackRequestDto: PlaybackRequestDto): Observable<string> {
    return this.http.post<any>(`${API_BASE_URL}api/Playback/digestToken`, playbackRequestDto).pipe(
      switchMap(res => {
        const authHeaderString = res.authorizationValue;

        if (!authHeaderString) {
          throw new Error("Property 'authorizationValue' missing in backend response");
        }

        const headers = new HttpHeaders().set('Authorization', authHeaderString);

        return this.externalClient.get(playbackRequestDto.url, {
          headers: headers,
          responseType: 'text'
        });
      }));
  }

  playStreamWithDigest(playbackRequestDto: PlaybackRequestDto): Observable<Blob> {

    return this.http.post<any>(`${API_BASE_URL}api/Playback/digestToken`, playbackRequestDto).pipe(
      switchMap(res => {
        const authHeaderString = res.authorizationValue;

        if (!authHeaderString) {
          throw new Error("Property 'authorizationValue' missing in backend response");
        }

        const headers = new HttpHeaders().set('Authorization', authHeaderString);

        return this.externalClient.get(playbackRequestDto.url, {
          headers: headers,
          responseType: 'blob'
        });
      }));
  }

  getPlaybackCamerasBySiteId(siteId: number): Observable<PlaybackCameraDetails[]> {
    let url = API_BASE_URL + `api/Playback/${siteId}/cameras`;
    url = url.replace(/[?&]$/, "");
    return this.http.get<PlaybackCameraDetails[]>(url);
  }

  getTimelapseCamerasBySiteId(siteId: number): Observable<TimelapseCameraDetails[]> {
    let url = API_BASE_URL + `api/Timelapse/${siteId}/cameras`;
    url = url.replace(/[?&]$/, "");
    return this.http.get<TimelapseCameraDetails[]>(url);
  }

  playCameraStream(parameter: PlaybackRequestDto): Observable<PlaybackStreamResponseDto> {
    return this.http.post<PlaybackStreamResponseDto>(`${API_BASE_URL}api/Playback/rtsp-urls`, parameter);
  }

  disconnectPlaybackWowzaStreambyName(parameter: PlaybackStreamsParameter): void {
    const url = `${API_BASE_URL}api/services/app/Wowza/PlaybackWowzaDisconnectById`;
  
    fetch(url, {
      keepalive: true,
      method: 'POST',
      body: JSON.stringify(parameter), // Convert object to JSON string
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.cookieService.get(environment.tokenKey)}`,
      }
    })
  }

  getPlaybackMostionsByStream(parameter: PlaybackMotionDto): Observable<OrchidMotionEventResponseDto> {
    let url = `${API_BASE_URL}api/Playback/orchid/motion`;
    url = url.replace(/[?&]$/, '');

    return this.http.post<OrchidMotionEventResponseDto>(url, parameter);
  }
  
  //#endregion Playback

  //#region GetContactsCustomers
  /**
 * This is the get function for get the  site customer contacts
 */
  getContactsCustomers(filter: CustomerContactsFilterModel): Observable<IPagedSiteContact> {
    let url = API_BASE_URL + "api/Contact/customer-contacts";
    url = url.replace(/[?&]$/, '');

    url = url + `?siteId=${filter.siteId}`;
    
    if (filter.skipCount) {
      url = url + `&skipCount=${filter.skipCount}`;
    }

    if (filter.maxResultCount) {
      url = url + `&maxResultCount=${filter.maxResultCount}`;
    }
    
    if (filter.searchText) {
      url = url + `&search=${filter.searchText}`;
    }

    return this.http.get<IPagedSiteContact>(url);
  }

  //#endregion GetContactsCustomers

  //#region Timelapse
  /**
   * Fetch Snapshots Token for Login
   */
  getSnapshotToken(): Observable<string> {
    let url = API_BASE_URL + `api/Timelapse/GetToken`;
    url = url.replace(/[?&]$/, "");
    
    return this.http.post<{ accessToken: string }>(url, { token: "" }).pipe(
      map(response => response.accessToken)
    );
  }

  /**
   * Refresh Snapshots Token
   */
  refreshSnapshotToken(oldToken: string): Observable<string> {
    let url = API_BASE_URL + `api/Timelapse/GetToken`;
    url = url.replace(/[?&]$/, "");
    
    return this.http.post<{ accessToken: string }>(url, { token: oldToken }).pipe(
      map(response => response.accessToken)
    );
  }

  /**
   * Get Timelapse URL for a given payload
  */
  getTimelapseImages(payload: GetTimelapseImagesPayload): Observable<GetTimelapseImagesResponse> {
    let url = API_BASE_URL + `api/Timelapse/GetTimelapse`;
    url = url.replace(/[?&]$/, "");

    return this.http.post<GetTimelapseImagesResponse>(url, payload);
  }

  /**
   * Get Date Range for a given device
   */
  getDateRangeForDevice(payload: GetDateRangeForDevicePayload): Observable<GetDateRangeForDeviceResponse> {
    let url = API_BASE_URL + `api/Timelapse/GetDateRangeForDevice`;
    url = url.replace(/[?&]$/, "");

    return this.http.post<GetDateRangeForDeviceResponse>(url, payload);
  }

  //#endregion Timelapse
  getSiteAlerts(siteId: number): Observable<SiteAlert[]> {
    const url = `${API_BASE_URL}api/Contact/${siteId}/alerts`;
    return this.http.get<SiteAlert[]>(url);
  }

  getSiteTimezones(siteId: number): Observable<SiteTimezone> {
    const url = `${API_BASE_URL}api/Contact/${siteId}/timezones`;
    return this.http.get<SiteTimezone>(url);
  }

  //#region Monthly Change Dashboard
  getMonthlyChangeDashboardData(filter: MonthlyDashboardFilterModel): Observable<MonthlyDashboardResponseModel> {
    let url = API_BASE_URL + "api/Contact/GetMonthlyDashboard";
    url = url.replace(/[?&]$/, "");

    url = url + `?siteId=${filter.siteId}`;
    
    if (filter.skipCount) {
      url = url + `&skipCount=${filter.skipCount}`;
    }

    if (filter.maxResultCount) {
      url = url + `&maxResultCount=${filter.maxResultCount}`;
    }

    if (filter.mnemonic) {
      url = url + `&mnemonic=${filter.mnemonic}`;
    }

    if (filter.startDate) {
      url = url + `&startDate=${filter.startDate}`;
    }

    if (filter.endDate) {
      url = url + `&endDate=${filter.endDate}`;
    }
    
    if (filter.submitedStartDate) {
      url = url + `&submitedStartDate=${filter.submitedStartDate}`;
    }

    if (filter.submitedEndDate) {
      url = url + `&submitedEndDate=${filter.submitedEndDate}`;
    }

    if (filter.region) {
      url = url + `&region=${filter.region}`;
    }

    if (filter.district) {
      url = url + `&district=${filter.district}`;
    }

    if (filter.action) {
      url = url + `&action=${filter.action}`;
    }

    if (filter.sorting) {
      url = url + `&sorting=${filter.sorting}`;
    }

    return this.http.get<MonthlyDashboardResponseModel>(url);
  }
  //#endregion Monthly Change Dashboard

  //#region Asset Dashboard
  getAssetDashboardData(filter: AssetDashboardFilterModel): Observable<AssetDashboardResponseModel> {
    let url = API_BASE_URL + "api/Contact/GetAssetDashboard";
    url = url.replace(/[?&]$/, "");

    url = url + `?siteId=${filter.siteId}`;

    if (filter.maxResultCount) {
      url = url + `&maxResultCount=${filter.maxResultCount}`;
    }

    if (filter.region) {
      url = url + `&region=${filter.region}`;
    }

    if (filter.district) {
      url = url + `&district=${filter.district}`;
    }

    if(filter.mnemonic) {
      url = url + `&mnemonic=${filter.mnemonic}`;
    }

    if (filter.startDate) {
      url = url + `&startDate=${filter.startDate}`;
    }

    if (filter.endDate) {
      url = url + `&endDate=${filter.endDate}`;
    }

    if (filter.sitename) {
      url = url + `&sitename=${filter.sitename}`;
    }

    if (filter.assetNumber) {
      url = url + `&assetnumber=${filter.assetNumber}`;
    }
    
    if (filter.chargeCode) {
      url = url + `&chargeCode=${filter.chargeCode}`;
    }

    if (filter.street) {
      url = url + `&street=${filter.street}`;
    }

    if (filter.city) {
      url = url + `&city=${filter.city}`;
    }

    if (filter.state) {
      url = url + `&state=${filter.state}`;
    }

    if (filter.leasestatus) {
      url = url + `&leasestatus=${filter.leasestatus}`;
    }

    if (filter.jobNumber) {
      url = url + `&jobNumber=${filter.jobNumber}`;
    }

    if (filter.zip) {
      url = url + `&zip=${filter.zip}`;
    }

    if (filter.skipCount) {
        url = url + `&skipCount=${filter.skipCount}`;
      }

    if (filter.sorting) {
      url = url + `&sorting=${filter.sorting}`;
    }

    return this.http.get<AssetDashboardResponseModel>(url);
  }
  //#endregion Asset Dashboard
  
  //#region Equipments
  /**
   * @param siteId
   *
   */
  async getEquipments(siteId: number, action: string): Promise<GetEquipmentsResponseModel> {
    const url = `${API_BASE_URL}api/Contact/GetEquipmentIntake/${siteId}?action=${encodeURIComponent(action)}`.replace(/[?&]$/, '');

    return await firstValueFrom(this.http.get<GetEquipmentsResponseModel>(url));
  }

  /**
   * @param addEquipmentsPayload
   * 
   */
  async addEquipments(addEquipmentsPayload: AddEquipmentsPayload): Promise<void> {
    const url = `${API_BASE_URL}api/Contact/save-intake-details`.replace(/[?&]$/, '');
    await firstValueFrom(this.http.post<void>(url, addEquipmentsPayload));
  }
  //#endregion Equipments

  //#region Opportunity
  /**
   * Create opportunity for an Equipment Intake
   * 
   */
  async createOpportunity(equipmentsPayload: AddEquipmentsPayload): Promise<CreateOpportunityResponseModel> {
    const url = `${API_BASE_URL}api/Contact/CreateOpportunity`.replace(/[?&]$/, '');
    return await firstValueFrom(this.http.post<CreateOpportunityResponseModel>(url, equipmentsPayload));
  }
  //#endregion Opportunity
}
